import React, { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { Pencil, Trash2, Plus, CheckCircle2 } from "lucide-react";
import { useLocation } from "react-router-dom";
import { format, parseISO } from "date-fns";

import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../../components/ui/select";
import {
	Table,
	TableHeader,
	TableBody,
	TableRow,
	TableHead,
	TableCell,
} from "../../components/ui/table";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import InvoiceModal from "../../components/InvoiceModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import useMoedaAtiva, {
	formatarMoeda,
	moedaPlataforma,
} from "../../hooks/useMoedaAtiva";

// Além do rótulo, cada situação diz em uma linha o que ela significa na
// prática — é o que aparece ao passar o mouse na etiqueta.
const SITUACAO = {
	open: {
		rotulo: "Aguardando pagamento",
		variante: "outline-solid",
		explicacao: "Ainda não caiu o dinheiro, mas também não venceu.",
	},
	paid: {
		rotulo: "Paga",
		variante: "secondary",
		explicacao: "Recebido. Nada a cobrar dessa empresa por esta fatura.",
	},
	overdue: {
		rotulo: "Atrasada",
		variante: "destructive",
		explicacao: "Passou do vencimento e ninguém pagou. É a hora de cobrar.",
	},
	canceled: {
		rotulo: "Cancelada",
		variante: "outline-solid",
		explicacao: "Foi anulada — a empresa não deve mais este valor.",
	},
};

// Cada fatura guarda a moeda com que nasceu: trocar o gateway hoje não
// reescreve o que já foi cobrado ontem em outra moeda.
const moeda = (valor, codigo) => formatarMoeda(valor, codigo);

const Invoices = () => {
	useMoedaAtiva();

	const location = useLocation();
	const companyIdUrl = new URLSearchParams(location.search).get("companyId");

	const [invoices, setInvoices] = useState([]);
	const [loading, setLoading] = useState(false);
	const [filtro, setFiltro] = useState("todas");
	const [modalOpen, setModalOpen] = useState(false);
	const [selecionada, setSelecionada] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const carregar = useCallback(async () => {
		setLoading(true);
		try {
			const { data } = await api.get("/invoices", {
				params: {
					companyId: companyIdUrl || undefined,
					status: filtro === "todas" ? undefined : filtro,
				},
			});
			setInvoices(data.invoices);
		} catch (err) {
			toastError(err);
		}
		setLoading(false);
	}, [filtro, companyIdUrl]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const fecharModal = () => {
		setModalOpen(false);
		setSelecionada(null);
		carregar();
	};

	const marcarPaga = async (invoice) => {
		try {
			await api.put(`/invoices/${invoice.id}`, { status: "paid" });
			toast.success("Fatura marcada como paga.");
			carregar();
		} catch (err) {
			toastError(err);
		}
	};

	const excluir = async (id) => {
		try {
			await api.delete(`/invoices/${id}`);
			toast.success("Fatura excluída.");
			carregar();
		} catch (err) {
			toastError(err);
		}
		setSelecionada(null);
	};

	const total = invoices.reduce((soma, i) => soma + Number(i.value || 0), 0);

	// Somar euro com real daria um número que não existe. Quando a tela mistura
	// moedas, a soma some — é melhor não ter total do que ter um total falso.
	const moedas = [
		...new Set(invoices.map((i) => i.currency || moedaPlataforma())),
	];
	const moedaDaSoma = moedas.length === 1 ? moedas[0] : null;

	return (
		<MainContainer>
			<ConfirmationModal
				title={selecionada && `Apagar a cobrança "${selecionada.detail}"?`}
				open={confirmOpen}
				onClose={() => {
					setConfirmOpen(false);
					setSelecionada(null);
				}}
				onConfirm={() => excluir(selecionada.id)}
			>
				Ela some da lista para sempre e não entra mais em nenhum total. Se a
				empresa simplesmente não vai pagar, o melhor é abrir a cobrança e marcar
				como cancelada — assim fica o registro do que aconteceu.
			</ConfirmationModal>

			<InvoiceModal
				open={modalOpen}
				onClose={fecharModal}
				invoiceId={selecionada?.id}
				companyId={companyIdUrl}
			/>

			<MainHeader>
				<Title>Faturas</Title>
				<MainHeaderButtonsWrapper>
					<Select value={filtro} onValueChange={setFiltro}>
						<SelectTrigger className="w-40">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="todas">Mostrar todas</SelectItem>
							<SelectItem value="open">Só as aguardando pagamento</SelectItem>
							<SelectItem value="paid">Só as já pagas</SelectItem>
							<SelectItem value="overdue">Só as atrasadas</SelectItem>
							<SelectItem value="canceled">Só as canceladas</SelectItem>
						</SelectContent>
					</Select>
					<Button
						onClick={() => {
							setSelecionada(null);
							setModalOpen(true);
						}}
					>
						<Plus className="h-4 w-4" />
						Cobrar uma empresa
					</Button>
				</MainHeaderButtonsWrapper>
			</MainHeader>

			<div className="flex-1 overflow-auto px-4 pb-4 sm:px-6 sm:pb-6">
				<p className="mb-4 max-w-3xl text-sm leading-relaxed text-muted-foreground">
					Cobranças das empresas clientes. A maioria nasce sozinha, pelo plano.
				</p>

				<div className="overflow-x-auto rounded-xl border bg-card">
					<Table>
						<TableHeader className="sticky top-0 z-10 bg-card">
							<TableRow>
								<TableHead>Do que se trata</TableHead>
								<TableHead>Quem deve pagar</TableHead>
								<TableHead className="text-right">Valor</TableHead>
								<TableHead className="text-center">Vence em</TableHead>
								<TableHead className="text-center">Situação</TableHead>
								<TableHead className="text-center">Ações</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{invoices.map((invoice) => {
								const situacao = SITUACAO[invoice.status] || SITUACAO.open;
								return (
									<TableRow key={invoice.id}>
										<TableCell className="font-medium">
											{invoice.detail}
										</TableCell>
										<TableCell>{invoice.company?.name || "—"}</TableCell>
										<TableCell className="text-right tabular-nums">
											{moeda(invoice.value, invoice.currency)}
										</TableCell>
										<TableCell className="text-center tabular-nums">
											{invoice.dueDate
												? format(parseISO(invoice.dueDate), "dd/MM/yyyy")
												: "—"}
										</TableCell>
										<TableCell className="text-center">
											<Badge
												variant={situacao.variante}
												title={situacao.explicacao}
											>
												{situacao.rotulo}
											</Badge>
										</TableCell>
										<TableCell>
											<div className="flex items-center justify-center gap-1">
												{invoice.status !== "paid" && (
													<Button
														variant="ghost"
														size="icon"
														className="h-8 w-8"
														aria-label="Dar baixa: já recebi este valor"
										title="Use quando o dinheiro entrou por fora (Pix, dinheiro, transferência). A cobrança para de ser enviada para a empresa."
														onClick={() => marcarPaga(invoice)}
													>
														<CheckCircle2 className="h-4 w-4" />
													</Button>
												)}
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Alterar esta cobrança"
										title="Mudar valor, vencimento ou descrição"
													onClick={() => {
														setSelecionada(invoice);
														setModalOpen(true);
													}}
												>
													<Pencil className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													className="h-8 w-8"
													aria-label="Apagar esta cobrança"
										title="Apagar esta cobrança"
													onClick={() => {
														setSelecionada(invoice);
														setConfirmOpen(true);
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
							{loading && <TableRowSkeleton columns={6} />}
							{!loading && invoices.length === 0 && (
								<TableRow>
									<TableCell
										colSpan={6}
										className="py-10 text-center text-sm text-muted-foreground"
									>
										{filtro === "todas"
											? "Nenhuma cobrança por aqui ainda. Elas aparecem sozinhas quando uma empresa tem plano e mensalidade."
											: "Nenhuma cobrança nesta situação. Troque o filtro ali em cima para ver as outras."}
									</TableCell>
								</TableRow>
							)}
							{!loading && invoices.length > 0 && (
								<TableRow className="bg-muted/40">
									<TableCell colSpan={2} className="font-medium">
										Soma do que está na tela
										<span className="ml-1 font-normal text-muted-foreground">
											(só destas {invoices.length} linhas, não do mês inteiro)
										</span>
									</TableCell>
									<TableCell className="text-right font-semibold tabular-nums">
										{moedaDaSoma ? (
											moeda(total, moedaDaSoma)
										) : (
											<span className="text-xs font-normal text-muted-foreground">
												moedas diferentes
											</span>
										)}
									</TableCell>
									<TableCell colSpan={3} />
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>
		</MainContainer>
	);
};

export default Invoices;
